import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiHome } from "react-icons/fi";
import { getOccupancySummary } from "../services/hostelService";

const sectionTitleStyle = {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--text-muted)",
    fontWeight: 700,
};

function OccupancyBar({ pct, color = "#003b67" }) {
    return (
        <div
            style={{
                height: 6,
                borderRadius: 4,
                background: `${color}18`,
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    height: "100%",
                    width: `${pct}%`,
                    background: color,
                    borderRadius: 4,
                    transition: "width 0.8s ease",
                }}
            />
        </div>
    );
}

// Self-contained widget — fetches its own data, no props required.
// Drop it in anywhere on the Dashboard, same pattern as
// <ScholarshipSummaryWidget /> and <UpcomingEventsWidget />.
export default function HostelSummaryWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        getOccupancySummary()
            .then((res) => setData(res.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="card" style={{ padding: "1.1rem 1.2rem", marginBottom: "1rem" }}>
                <div style={sectionTitleStyle}>Hostel Occupancy</div>
                <div style={{ color: "var(--text-muted)", fontSize: 13, paddingTop: 6 }}>
                    Loading...
                </div>
            </div>
        );
    }

    if (error || !data) return null; // fail quietly — don't break the rest of the dashboard

    const { totals, hostels } = data;

    if (totals.totalHostels === 0) {
        return (
            <div className="card" style={{ padding: "1.1rem 1.2rem", marginBottom: "1rem" }}>
                <div style={sectionTitleStyle}>Hostel Occupancy</div>
                <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0.5rem 0 0" }}>
                    No hostels set up yet.{" "}
                    <Link to="/hostels" style={{ fontWeight: 600 }}>
                        Add one
                    </Link>
                </p>
            </div>
        );
    }

    return (
        <div className="card" style={{ padding: "1.1rem 1.2rem", marginBottom: "1rem" }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "0.6rem",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span
                        style={{
                            width: 28,
                            height: 28,
                            borderRadius: 7,
                            background: "#003b6718",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <FiHome size={15} color="#003b67" />
                    </span>
                    <div style={sectionTitleStyle}>Hostel Occupancy</div>
                </div>
                <Link to="/hostels" className="btn btn-outline" style={{ fontSize: 12, padding: "4px 10px" }}>
                    View All
                </Link>
            </div>

            <div style={{ display: "flex", gap: "1.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
                <div>
                    <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1 }}>
                        {totals.occupancyPct}%
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>overall occupied</div>
                </div>
                <div>
                    <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1 }}>
                        {totals.occupiedBeds}/{totals.totalCapacity}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>beds filled</div>
                </div>
                <div>
                    <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1, color: "#059669" }}>
                        {totals.vacantBeds}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>vacant beds</div>
                </div>
            </div>

            <div style={{ display: "grid", gap: "0.5rem" }}>
                {hostels.map((h) => (
                    <div key={h.hostelId}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: 12,
                                marginBottom: 3,
                            }}
                        >
                            <span style={{ fontWeight: 600 }}>{h.name}</span>
                            <span style={{ color: "var(--text-muted)" }}>
                                {h.occupiedBeds}/{h.totalCapacity} ({h.occupancyPct}%)
                            </span>
                        </div>
                        <OccupancyBar
                            pct={h.occupancyPct}
                            color={h.occupancyPct >= 90 ? "#b91c1c" : "#003b67"}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}
